import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { getMyCreations, callSpecialistAgent, sendFeedback, getGenome } from '../api';
import { FaPaperPlane, FaThumbsUp, FaThumbsDown, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import { GiMicroscope } from 'react-icons/gi';

import AgentCard from '../components/AgentCard';
import CardGrid from '../components/CardGrid';
import Modal from '../components/Modal'; // <-- IMPORT MODAL

// --- Styled Components (unchanged) ---
const ChatContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: 75vh;
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 8px;
  background: ${props => props.theme.colors.surface};
  box-shadow: ${props => props.theme.shadow};
`;
const ChatHeader = styled.h2`
  text-align: center;
  padding: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.surface2};
  margin: 0;
  color: ${props => props.theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;
const MessageList = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
const Message = styled.div`
  padding: 0.75rem 1.25rem;
  border-radius: 18px;
  max-width: 80%;
  line-height: 1.5;
  white-space: pre-wrap;
  
  &.user {
    background: ${props => props.theme.colors.primary};
    color: white;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
  }
  
  &.model {
    background: ${props => props.theme.colors.surface2};
    color: ${props => props.theme.colors.text};
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  }
`;
const InputArea = styled.form`
  display: flex;
  padding: 1rem;
  border-top: 1px solid ${props => props.theme.colors.surface2};
`;
const ChatInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 20px;
  color: ${props => props.theme.colors.text};
  margin-right: 0.5rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const SendButton = styled.button`
  padding: 0.75rem 1rem;
  border: none;
  background: ${props => props.theme.colors.primary};
  color: white;
  border-radius: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled {
    background: ${props => props.theme.colors.textSecondary};
  }
`;
const FeedbackSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;
const ThumbButton = styled.button`
  background: ${props => props.$active ? (props.$rating === 'good' ? props.theme.colors.accent : props.theme.colors.error) : 'transparent'};
  border: 1px solid ${props => props.$active ? (props.$rating === 'good' ? props.theme.colors.accent : props.theme.colors.error) : props.theme.colors.surface2};
  color: ${props => props.$active ? 'white' : props.theme.colors.textSecondary};
  border-radius: 50%;
  width: 30px;
  height: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${props => props.theme.colors.text};
    border-color: ${props => props.theme.colors.text};
  }
`;
const ErrorMsg = styled.div`
  color: ${props => props.theme.colors.error};
  background: rgba(248, 81, 73, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 5px;
  margin: 1rem;
`;

// --- Feedback Toast ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;
const fadeOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(20px); }
`;
const FeedbackToast = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  background: ${props => props.theme.colors.accent};
  color: ${props => props.theme.colors.background};
  padding: 1rem 1.5rem;
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  z-index: 2000;
  animation: ${fadeIn} 0.3s ease-out forwards;
  
  &.error {
    background: ${props => props.theme.colors.error};
    color: white;
  }
`;

// --- Feedback Modal Styles ---
const ModalTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
  font-family: inherit;
  margin-top: 1rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ModalButton = styled.button`
  width: 100%;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  &:disabled {
    background: ${props => props.theme.colors.textSecondary};
  }
`;

// --- Page Component ---
const TestPage = () => {
  const { genomeId } = useParams();
  const navigate = useNavigate();
  const [creations, setCreations] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Feedback states
  const [feedback, setFeedback] = useState({}); // { messageId: "good" | "bad" }
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null); // { message, rating }
  const [feedbackComment, setFeedbackComment] = useState('');

  const messageListRef = useRef(null);

  useEffect(() => {
    if (messageListRef.current) {
      messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (!genomeId) {
      setPageLoading(true);
      getMyCreations()
        .then(res => setCreations(res.data))
        .catch(err => setError("Failed to fetch creations"))
        .finally(() => setPageLoading(false));
    } else {
      setPageLoading(true);
      getGenome(genomeId)
        .then(res => setCurrentAgent(res.data))
        .catch(err => setError("Failed to fetch genome"))
        .finally(() => setPageLoading(false));
    }
  }, [genomeId]);
  
  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !currentAgent?.metadata.deploymentUrl) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);
    
    try {
      const { data } = await callSpecialistAgent(
        currentAgent.metadata.deploymentUrl,
        currentInput
      );
      
      const responseText = data.response || data.text || JSON.stringify(data);
      const modelResponse = { role: 'model', content: responseText, id: Date.now() };
      setMessages(prev => [...prev, modelResponse]);
      
    } catch (err) {
      console.error("Failed to call specialist agent", err);
      const errorText = err.response?.data?.detail || err.message;
      setMessages(prev => [...prev, { role: 'model', content: `Error: ${errorText}`, id: Date.now() }]);
    } finally {
      setIsLoading(false);
    }
  };
  
  // --- UPDATED FEEDBACK HANDLER ---
  const openFeedbackModal = (message, rating) => {
    if (feedback[message.id] === rating) { // If clicking same button, toggle off
        setFeedback(prev => ({ ...prev, [message.id]: null }));
        setCurrentFeedback(null);
        setIsFeedbackModalOpen(false);
        return;
    }
    setFeedback(prev => ({ ...prev, [message.id]: rating })); // Set active state
    setCurrentFeedback({ message, rating }); // Set data for modal
    setFeedbackComment(''); // Clear old comment
    setIsFeedbackModalOpen(true); // Open modal
  };

  const submitFeedback = () => {
    if (!currentFeedback) return;
    
    const { message, rating } = currentFeedback;
    const lastUserMessage = [...messages].reverse().find(m => m.role === 'user');
    
    const feedbackPayload = {
      user_id: "placeholder-user-id",
      session_id: "placeholder-session-id",
      user_input: lastUserMessage?.content || "N/A",
      model_output: message.content,
      rating: rating,
      comment: feedbackComment
    };
    
    setIsFeedbackModalOpen(false); // Close modal
    
    sendFeedback(currentAgent.metadata.deploymentUrl, feedbackPayload)
      .then(() => {
        setFeedbackMessage('Feedback sent successfully!');
        setTimeout(() => setFeedbackMessage(''), 3000);
      })
      .catch(err => {
        setFeedbackMessage('Error sending feedback: ' + err.message);
        setTimeout(() => setFeedbackMessage(''), 3000);
      });
  };

  // RENDER LOGIC
  if (pageLoading) return <div>Loading...</div>;
  if (error) return <ErrorMsg>{error}</ErrorMsg>;

  if (!genomeId) {
    return (
      <CardGrid>
        {creations.map(genome => (
          <AgentCard 
            key={genome.metadata.genomeId} 
            genome={genome}
            onClick={() => navigate(`/test/${genome.metadata.genomeId}`)}
          />
        ))}
      </CardGrid>
    );
  }

  return (
    <>
      <ChatContainer>
        <ChatHeader>
          <GiMicroscope /> Testing: {currentAgent?.manifest.name || '...'}
        </ChatHeader>
        <MessageList ref={messageListRef}>
          {messages.map((msg, index) => (
            <Message key={index} className={msg.role}>
              <div>{msg.content}</div>
              {msg.role === 'model' && msg.id && (
                <FeedbackSection>
                  <ThumbButton 
                    onClick={() => openFeedbackModal(msg, 'good')} 
                    $active={feedback[msg.id] === 'good'}
                    $rating="good"
                  >
                    <FaThumbsUp />
                  </ThumbButton>
                  <ThumbButton 
                    onClick={() => openFeedbackModal(msg, 'bad')}
                    $active={feedback[msg.id] === 'bad'}
                    $rating="bad"
                  >
                    <FaThumbsDown />
                  </ThumbButton>
                </FeedbackSection>
              )}
            </Message>
          ))}
          {isLoading && <Message className="model">...</Message>}
        </MessageList>
        <InputArea onSubmit={handleSend}>
          <ChatInput 
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Send a message..."
            disabled={!currentAgent?.metadata.deploymentUrl}
          />
          <SendButton type="submit" disabled={isLoading || !currentAgent?.metadata.deploymentUrl}>
            <FaPaperPlane />
          </SendButton>
        </InputArea>
      </ChatContainer>
      
      {/* --- RENDER THE TOAST --- */}
      {feedbackMessage && (
        <FeedbackToast className={feedbackMessage.startsWith('Error') ? 'error' : ''}>
          {feedbackMessage.startsWith('Error') ? <FaExclamationCircle /> : <FaCheckCircle />} 
          {feedbackMessage}
        </FeedbackToast>
      )}

      {/* --- RENDER THE FEEDBACK MODAL --- */}
      <Modal 
        title={`Submit Feedback (${currentFeedback?.rating || ''})`}
        isOpen={isFeedbackModalOpen}
        onClose={() => setIsFeedbackModalOpen(false)}
      >
        <p style={{color: 'white'}}>Please provide a brief comment:</p>
        <ModalTextArea
          value={feedbackComment}
          onChange={(e) => setFeedbackComment(e.target.value)}
          placeholder="e.g., The response was too long..."
        />
        <ModalButton onClick={submitFeedback}>
          Submit Feedback
        </ModalButton>
      </Modal>
    </>
  );
};

export default TestPage;